import { useEffect } from 'react'
import { Outlet, useLocation, useMatches } from 'react-router'
import MaintenancePage from '@/features/errors/pages/MaintenancePage'
import { useAuth } from '@/lib/auth'
import { communitySlugFromPath, rememberCommunity, useCommunity } from '@/lib/community'
import { maintenancePhase, usePlatformStatus } from '@/lib/platform'
import { useUserChannel } from '@/lib/realtime'
import { activeTabFor } from './shell-utils'
import { Footer } from './Footer'
import { GlobalBanners } from './GlobalBanners'
import { MobileBottomNav } from './MobileBottomNav'
import { SkipLink } from './SkipLink'
import { SingleSlot } from './slots'
import { TopBar } from './TopBar'
import type { ModalLocationState, RouteHandle } from './types'

/** The app shell (UI.md section 3): top bar, global banners, page, footer, bottom nav. */
export function AppShell() {
  const location = useLocation()
  const { me, isPlatformAdmin } = useAuth()
  const status = usePlatformStatus()
  const pathSlug = communitySlugFromPath(location.pathname)
  // An unknown slug is no community: platform chrome around the not found page, never an identity made up from
  // the URL, and it is not remembered as the last community (Q-TRUST-09).
  const { community, notFound } = useCommunity(pathSlug ?? undefined)
  const slug = pathSlug && !notFound ? pathSlug : null
  const shown = (location.state as ModalLocationState | null)?.backgroundLocation ?? location
  const tab = slug ? activeTabFor(shown.pathname, slug) : null
  const fullHeight = useMatches().some((m) => (m.handle as RouteHandle | undefined)?.fullHeight)

  // Own user channel: unread badges, notifications and chat stay live.
  useUserChannel(me?.user.id)

  // Remember a community once it has loaded (canonical slug), so a mistyped URL never becomes the last one.
  const loadedSlug = slug ? community?.slug : undefined
  useEffect(() => {
    if (loadedSlug && me) rememberCommunity(loadedSlug)
  }, [loadedSlug, me])

  if (maintenancePhase(status) === 'active' && !isPlatformAdmin) {
    return <MaintenancePage message={status.maintenance?.message} />
  }

  return (
    // The bottom nav (members, below 768 px) is fixed: pad the whole shell, not just main, so the page footer
    // with Privacy, Terms and Support ends above it too (Q-SRCH-27). --mobile-nav-h is 0 while the nav is hidden
    // for the on screen keyboard, so no empty strip stays under a chat composer (UXA-20).
    <div className="flex min-h-dvh flex-col bg-page pb-(--mobile-nav-h) text-text">
      <SkipLink />
      <TopBar communitySlug={slug} tab={tab} />
      {/* Inside a community the banners sit under the tab row (CommunityLayout). */}
      {slug ? null : <GlobalBanners />}
      <main
        id="main-content"
        tabIndex={-1}
        className="flex flex-1 flex-col outline-none"
      >
        <Outlet />
      </main>
      {/* Fixed height screens (chat) leave the footer out (Q-UX-38). */}
      {!slug && !fullHeight ? <Footer /> : null}
      <MobileBottomNav communitySlug={slug} />
      <SingleSlot name="LevelUpToast" props={{}} />
      <SingleSlot name="ShortcutsProvider" props={{}} />
    </div>
  )
}
